import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import './ServicePage.css';
import './PerformanceMarketingPage.css';

const SEO = {
  title: 'Performance Marketing & Digital Advertising Services | Mad Marketing Club',
  description: 'Drive targeted traffic and scale revenue with Google Ads, Meta Ads, and marketplace advertising. Data-driven performance marketing for D2C brands, e-commerce, and enterprises.',
  canonical: 'https://madmarketingclub.com/services/performance-marketing',
  breadcrumb: [
    { name: 'Home',     url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Performance Marketing', url: 'https://madmarketingclub.com/services/performance-marketing' },
  ],
  service: { name: 'Performance Marketing & Digital Advertising Services', type: 'Digital Advertising' },
};

const HERO_PLATFORMS = [
  { label: 'Google Ads',         color: '#4285f4', delay: '3s' },
  { label: 'Meta Ads',           color: '#1877f2', delay: '4s' },
  { label: 'Instagram Ads',      color: '#e1306c', delay: '5s' },
  { label: 'Amazon Advertising', color: '#ff9900', delay: '3.5s' },
  { label: 'Flipkart Ads',       color: '#2874f0', delay: '4.5s' },
  { label: 'YouTube Ads',        color: '#ff0000', delay: '5.5s' },
];

const FOCUS_AREAS = [
  'Lead generation',
  'E-commerce sales growth',
  'Brand awareness',
  'Customer acquisition',
  'Retargeting & remarketing',
  'Conversion optimization',
  'Revenue scaling',
  'Multi-platform growth',
];

const GOOGLE_SERVICES = [
  'Google Search Ads',
  'Google Shopping Ads',
  'Performance Max Campaigns',
  'Display Advertising',
  'YouTube Video Ads',
  'App Promotion Campaigns',
  'Local Service Ads',
  'Retargeting Campaigns',
  'Lead Generation Campaigns',
  'Conversion Tracking Setup',
  'Google Analytics Integration',
  'Keyword Research & Planning',
  'Competitor Analysis',
  'Bid Strategy Optimization',
  'Landing Page Optimization',
];

const META_IDEAL = [
  'E-commerce sales',
  'Brand awareness',
  'Retargeting campaigns',
  'Lead generation',
  'Product launches',
  'Customer engagement',
  'D2C brand growth',
  'Visual storytelling campaigns',
];

const META_SERVICES = [
  'Facebook Ads Management',
  'Instagram Ads Management',
  'Conversion Campaigns',
  'Lead Generation Ads',
  'Remarketing Campaigns',
  'Catalog Sales Campaigns',
  'Dynamic Product Ads',
  'Video Advertising',
  'Creative Testing',
  'Audience Research',
  'Pixel Setup & Tracking',
  'Conversion API Setup',
  'Funnel Optimization',
  'Campaign Scaling Strategies',
];

const MARKETPLACES = [
  'Amazon Ads',
  'Flipkart Ads',
  'Meesho Ads',
  'Myntra Advertising',
  'Nykaa Promotions',
  'Walmart Connect',
  'eBay Promoted Listings',
  'Noon Ads',
  'Blinkit Promotions',
  'AJIO Campaign Visibility',
];

const MARKETPLACE_BENEFITS = [
  'Product visibility',
  'Search rankings',
  'Conversion rates',
  'Sales velocity',
  'Brand awareness',
  'Marketplace positioning',
];

const ECOM_SERVICES = [
  'Product Feed Optimization',
  'Shopping Campaigns',
  'Catalog Ads',
  'Retargeting Funnels',
  'Abandoned Cart Campaigns',
  'Product Launch Campaigns',
  'Seasonal Sales Campaigns',
  'Customer Retention Advertising',
  'Conversion Rate Optimization',
  'Landing Page Strategy',
];

const PROCESS = [
  {
    num: '01',
    title: 'Business & Audience Research',
    intro: 'We begin by understanding:',
    points: [
      'Business goals',
      'Customer personas',
      'Competitor landscape',
      'Market positioning',
      'Product or service offerings',
      'Current advertising performance',
      'Revenue targets',
    ],
  },
  {
    num: '02',
    title: 'Advertising Strategy Development',
    intro: 'Our team creates:',
    points: [
      'Platform selection strategy',
      'Audience targeting roadmap',
      'Funnel strategy',
      'Creative direction',
      'Budget allocation plans',
      'KPI & ROAS targets',
    ],
  },
  {
    num: '03',
    title: 'Campaign Setup & Tracking',
    intro: 'We implement:',
    points: [
      'Ad account setup',
      'Pixel & conversion tracking',
      'Campaign structure',
      'Audience segmentation',
      'Ad creative deployment',
      'Landing page alignment',
      'Analytics integrations',
    ],
  },
  {
    num: '04',
    title: 'Optimization & Scaling',
    intro: 'We continuously optimize:',
    points: [
      'Bidding strategies',
      'Audience performance',
      'Ad creatives',
      'CTR & CPC performance',
      'Conversion rates',
      'ROAS efficiency',
      'Budget distribution',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Growth Analysis',
    intro: 'Our reporting includes:',
    points: [
      'Campaign performance reports',
      'ROAS analysis',
      'Conversion tracking',
      'Funnel analytics',
      'Audience insights',
      'Revenue growth reporting',
      'Scaling opportunities',
    ],
  },
];

const DIFFERENTIATORS = [
  'Multi-platform advertising expertise',
  'Performance-focused campaign management',
  'Advanced audience targeting',
  'Creative strategy optimization',
  'Conversion-focused execution',
  'Continuous testing & scaling',
  'Detailed analytics & reporting',
  'ROI-driven growth systems',
];

const WHY_STATS = [
  { val: '4.2X', lbl: 'Average ROAS' },
  { val: '300%', lbl: 'Revenue Growth' },
  { val: '60%',  lbl: 'CTR Improvement' },
  { val: '500+', lbl: 'Campaigns Managed' },
];

const INDUSTRIES = [
  'E-commerce Brands',
  'Fashion & Apparel',
  'Beauty & Skincare',
  'Health & Wellness',
  'SaaS Companies',
  'Real Estate',
  'Education & EdTech',
  'Local Businesses',
  'Restaurants & Cafes',
  'Automotive Brands',
  'Consumer Electronics',
  'D2C Startups',
];

const ADVANCED = [
  {
    title: 'Conversion Rate Optimization (CRO)',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Funnel optimization',
      'UX improvements',
      'A/B testing',
      'Checkout optimization',
      'Mobile performance enhancement',
    ],
  },
  {
    title: 'Retargeting & Customer Retention',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
    points: [
      'Dynamic remarketing',
      'Cart abandonment campaigns',
      'Customer retention ads',
      'Loyalty campaigns',
      'Upsell & cross-sell advertising',
    ],
  },
  {
    title: 'Analytics & Attribution',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Google Analytics',
      'Conversion tracking',
      'Attribution modeling',
      'Funnel tracking',
      'Customer journey analysis',
      'Revenue attribution systems',
    ],
  },
];

const SEO_BENEFITS = [
  'Increasing brand searches',
  'Improving audience engagement',
  'Supporting product launches',
  'Accelerating customer acquisition',
  'Generating first-party audience data',
  'Enhancing omnichannel visibility',
];

const FAQS = [
  {
    q: 'Which advertising platform is best for my business?',
    a: 'It depends on your goals. Google Ads is ideal for high-intent searches, Meta Ads works well for audience targeting and brand growth, while marketplace advertising is best for e-commerce sales within shopping platforms.',
  },
  {
    q: 'Do you manage both lead generation and e-commerce campaigns?',
    a: 'Yes. We manage campaigns for lead generation, local businesses, service-based companies, and e-commerce brands.',
  },
  {
    q: 'Can you manage advertising for marketplaces like Amazon and Flipkart?',
    a: 'Yes. We manage marketplace advertising campaigns including Amazon Ads, Flipkart Ads, Walmart Connect, eBay Promoted Listings, and more.',
  },
  {
    q: 'Do you provide creatives for advertising campaigns?',
    a: 'Yes. We assist with ad creatives, banners, video strategy, copywriting, and conversion-focused content.',
  },
  {
    q: 'How do you measure campaign performance?',
    a: 'We track ROAS, conversions, CTR, CPC, CPA, revenue growth, customer acquisition cost, and overall business performance metrics.',
  },
];

const TABS = [
  { key: 'google',      label: 'Google Ads' },
  { key: 'meta',        label: 'Meta Ads' },
  { key: 'marketplace', label: 'Marketplace Ads' },
];

export default function PerformanceMarketingPage() {
  const [activePlatform, setActivePlatform] = useState('google');
  const [transitioning, setTransitioning]   = useState(false);
  const [openFaq, setOpenFaq]               = useState(null);
  const [openStep, setOpenStep]             = useState(0);

  const switchPlatform = (p) => {
    if (p === activePlatform) return;
    setTransitioning(true);
    setTimeout(() => { setActivePlatform(p); setTransitioning(false); }, 150);
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Performance Marketing</span>
          </div>
        </nav>

        {/* ── HERO ── */}
        <section className="pm-hero">
          <div className="pm-orb pm-orb-1" />
          <div className="pm-orb pm-orb-2" />
          <div className="pm-orb pm-orb-3" />

          <div className="pm-hero-inner">
            {/* Floating platform chips */}
            <div className="pm-hero-badges">
              {HERO_PLATFORMS.map((p, i) => (
                <span
                  key={i}
                  className="pm-hero-badge"
                  style={{ '--badge-delay': p.delay }}
                >
                  <span className="pm-hero-badge-dot" style={{ background: p.color }} />
                  {p.label}
                </span>
              ))}
            </div>

            <div className="sp-hero-tag">Performance Marketing</div>
            <h1 className="pm-hero-h1">
              Performance Marketing &amp; Digital Advertising Services
            </h1>
            <p className="pm-hero-sub">
              Google Ads, Meta Ads &amp; Marketplace Advertising Solutions for Scalable Business Growth
            </p>
            <p className="sp-hero-desc">
              Drive targeted traffic, generate qualified leads, increase online sales, and scale your brand with data-driven digital advertising services. Our performance marketing solutions combine Google Ads, Meta Ads, and marketplace advertising strategies to help businesses achieve measurable growth across search engines, social media platforms, and e-commerce marketplaces.
            </p>

            <div className="pm-hero-stats">
              {WHY_STATS.map((s, i) => (
                <div key={i} className="pm-hero-stat">
                  <span className="pm-hero-stat-val">{s.val}</span>
                  <span className="pm-hero-stat-lbl">{s.lbl}</span>
                </div>
              ))}
            </div>

            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Strategy</a>
              <a href="#ecosystem" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* ── FULL-FUNNEL OVERVIEW ── */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-label">Full-Funnel Advertising Solutions</div>
            <h2 className="sp-h2">Reach Customers Across Every Digital Touchpoint</h2>
            <p className="sp-para">
              Modern digital advertising is not just about running ads — it is about building a scalable customer acquisition system. We help businesses create high-performing campaigns across search, social, and marketplaces using audience targeting, conversion optimization, data analytics, and creative strategy.
            </p>
            <p className="sp-para">Our advertising services focus on:</p>
            <div className="pm-focus-grid">
              {FOCUS_AREAS.map((item, i) => (
                <div key={i} className="pm-focus-item">
                  <span className="pm-focus-dot" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── ADVERTISING ECOSYSTEM (TABS) ── */}
        <section className="sp-sec sp-sec--alt" id="ecosystem">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Advertising Ecosystem</div>
            <h2 className="sp-h2">Multi-Platform Advertising Solutions</h2>

            <div className="pm-tabs-wrap">
              {/* Tab nav */}
              <div className="pm-tabs-nav">
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    className={`pm-tab-btn pm-tab-btn--${tab.key}${activePlatform === tab.key ? ' pm-tab-btn--active' : ''}`}
                    onClick={() => switchPlatform(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className={`pm-tab-content${transitioning ? ' pm-tab-content--fade' : ''}`}>

                {/* GOOGLE ADS */}
                {activePlatform === 'google' && (
                  <div className="pm-platform-panel pm-platform-panel--google">
                    <div className="pm-platform-header">
                      <div className="pm-platform-icon pm-platform-icon--google">G</div>
                      <div>
                        <h3 className="pm-platform-title">Google Ads Services</h3>
                        <p className="pm-platform-sub">Capture High-Intent Customers Through Search</p>
                      </div>
                    </div>
                    <p className="sp-para">
                      Google Ads helps businesses appear in front of users actively searching for products, services, and solutions online. Google advertising enables businesses to target customers through Search, Shopping, YouTube, Display, Discover, and Performance Max campaigns.
                    </p>
                    <div className="pm-services-label">Our Google Ads Services Include</div>
                    <div className="pm-services-grid">
                      {GOOGLE_SERVICES.map((s, i) => (
                        <div key={i} className="pm-service-chip pm-service-chip--google">{s}</div>
                      ))}
                    </div>
                    <div className="pm-targeting-note pm-targeting-note--google">
                      Google Ads allows businesses to target users based on keywords, demographics, interests, location, device behavior, and purchase intent.
                    </div>
                  </div>
                )}

                {/* META ADS */}
                {activePlatform === 'meta' && (
                  <div className="pm-platform-panel pm-platform-panel--meta">
                    <div className="pm-platform-header">
                      <div className="pm-platform-icon pm-platform-icon--meta">f</div>
                      <div>
                        <h3 className="pm-platform-title">Meta Ads Services</h3>
                        <p className="pm-platform-sub">Scale Your Brand Through Facebook &amp; Instagram Advertising</p>
                      </div>
                    </div>
                    <p className="sp-para">
                      Meta Platforms advertising enables brands to reach highly targeted audiences across Facebook, Instagram, Messenger, and Audience Network platforms.
                    </p>
                    <div className="pm-services-label">Meta Ads are ideal for:</div>
                    <div className="pm-ideal-grid">
                      {META_IDEAL.map((item, i) => (
                        <div key={i} className="pm-ideal-chip">{item}</div>
                      ))}
                    </div>
                    <div className="pm-services-label">Our Meta Ads Services Include</div>
                    <div className="pm-services-grid">
                      {META_SERVICES.map((s, i) => (
                        <div key={i} className="pm-service-chip pm-service-chip--meta">{s}</div>
                      ))}
                    </div>
                    <div className="pm-targeting-note pm-targeting-note--meta">
                      Meta advertising platforms offer advanced audience targeting based on interests, demographics, behaviors, engagement, and customer activity.
                    </div>
                  </div>
                )}

                {/* MARKETPLACE ADS */}
                {activePlatform === 'marketplace' && (
                  <div className="pm-platform-panel pm-platform-panel--marketplace">
                    <div className="pm-platform-header">
                      <div className="pm-platform-icon pm-platform-icon--marketplace">M</div>
                      <div>
                        <h3 className="pm-platform-title">Marketplace Advertising Services</h3>
                        <p className="pm-platform-sub">Increase Product Visibility Across E-commerce Platforms</p>
                      </div>
                    </div>
                    <p className="sp-para">
                      Marketplace advertising helps brands improve product discoverability, rankings, and conversions directly within e-commerce platforms where customers are actively shopping.
                    </p>
                    <div className="pm-services-label">We manage advertising campaigns across:</div>
                    <div className="pm-marketplace-grid">
                      {MARKETPLACES.map((m, i) => (
                        <div key={i} className="pm-marketplace-item">{m}</div>
                      ))}
                    </div>
                    <div className="pm-services-label">Marketplace ads help brands improve:</div>
                    <div className="pm-benefits-grid">
                      {MARKETPLACE_BENEFITS.map((b, i) => (
                        <div key={i} className="pm-benefit-chip">{b}</div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </section>

        {/* ── E-COMMERCE ADVERTISING ── */}
        <section className="sp-sec" id="ecommerce">
          <div className="sp-sec-inner">
            <div className="sp-label">E-commerce Advertising Solutions</div>
            <h2 className="sp-h2">Performance Marketing for Online Stores &amp; D2C Brands</h2>
            <p className="sp-para">
              We help Shopify stores, marketplace sellers, and D2C brands create integrated advertising systems that combine paid traffic, retargeting, customer retention, and conversion optimization.
            </p>
            <h3 className="sp-h3">E-commerce Advertising Services Include</h3>
            <div className="pm-ecom-grid">
              {ECOM_SERVICES.map((item, i) => (
                <div key={i} className="pm-ecom-item">
                  <span className="sp-check" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── PROCESS ── */}
        <section className="sp-sec sp-sec--alt" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Advertising Process</div>
            <h2 className="sp-h2">How We Drive Results</h2>
            <div className="sp-proc-acc">
              {PROCESS.map((step, i) => (
                <div key={i} className={`sp-proc-acc-item${openStep === i ? ' sp-proc-acc-item--open' : ''}`}>
                  <button
                    className="sp-proc-acc-header"
                    onClick={() => setOpenStep(openStep === i ? null : i)}
                    aria-expanded={openStep === i}
                  >
                    <span className="sp-proc-acc-num">{step.num}</span>
                    <span className="sp-proc-acc-title">{step.title}</span>
                    <svg className="sp-proc-acc-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {openStep === i && (
                    <div className="sp-proc-acc-body">
                      <p className="sp-proc-acc-intro">{step.intro}</p>
                      <ul className="sp-proc-acc-grid">
                        {step.points.map((pt, j) => (
                          <li key={j} className="sp-proc-acc-pt">
                            <span className="sp-dot-sm" />{pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY CHOOSE ── */}
        <section className="sp-sec" id="why">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Businesses Choose Our Advertising Services</div>
            <h2 className="sp-h2">Data-Driven Performance Marketing Strategies</h2>
            <p className="sp-para">
              We combine analytics, creative strategy, audience targeting, and conversion optimization to help businesses scale profitably through digital advertising.
            </p>

            <div className="pm-why-grid">
              <div>
                <h3 className="sp-h3">What Makes Our Approach Different?</h3>
                <ul className="pm-diff-list">
                  {DIFFERENTIATORS.map((d, i) => (
                    <li key={i} className="pm-diff-item">
                      <span className="sp-check" />{d}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pm-stats-display">
                {WHY_STATS.map((s, i) => (
                  <div key={i} className="pm-stat-card">
                    <span className="pm-stat-val">{s.val}</span>
                    <span className="pm-stat-lbl">{s.lbl}</span>
                  </div>
                ))}
              </div>
            </div>

            <h3 className="sp-h3">Industries We Work With</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => (
                <span key={i} className="sp-industry-chip">{ind}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ── ADVANCED SOLUTIONS ── */}
        <section className="sp-sec sp-sec--alt" id="advanced">
          <div className="sp-sec-inner">
            <div className="sp-label">Advanced Advertising Solutions</div>
            <h2 className="sp-h2">Beyond Campaigns — Integrated Growth Systems</h2>
            <div className="pm-advanced-grid">
              {ADVANCED.map((card, i) => (
                <div key={i} className="pm-advanced-card">
                  <div className="pm-advanced-accent" style={{ background: card.gradient }} />
                  <div className="pm-advanced-content">
                    <div className="pm-advanced-icon" style={{ background: card.gradient }}>
                      {card.icon}
                    </div>
                    <h3 className="pm-advanced-title">{card.title}</h3>
                    <ul className="pm-advanced-list">
                      {card.points.map((pt, j) => (
                        <li key={j} className="pm-advanced-item">
                          <span className="sp-dot-sm" />{pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SEO BENEFITS ── */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">SEO Benefits of Paid Advertising</div>
            <h2 className="sp-h2">Paid Advertising Supports Your Entire Digital Growth</h2>
            <p className="sp-para">
              Paid advertising can also support overall digital growth by:
            </p>
            <div className="pm-seo-grid">
              {SEO_BENEFITS.map((benefit, i) => (
                <div key={i} className="pm-seo-item">
                  <span className="pm-seo-dot" />
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button
                    className="sp-faq-header"
                    onClick={() => toggleFaq(i)}
                    aria-expanded={openFaq === i}
                  >
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="sp-faq-body">
                      <div className="sp-faq-a">{faq.a}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="pm-cta">
          <div className="pm-cta-orb pm-cta-orb-1" />
          <div className="pm-cta-orb pm-cta-orb-2" />
          <div className="pm-cta-inner">
            <div className="pm-cta-badge">Scale Your Business</div>
            <h2 className="pm-cta-h2">Ready to Scale Your Business Through Advertising?</h2>
            <p className="pm-cta-desc">
              Grow faster with performance-driven digital advertising strategies focused on visibility, conversions, customer acquisition, and long-term revenue growth. Our Google Ads, Meta Ads, and marketplace advertising services help businesses create scalable growth systems across search, social, and e-commerce platforms.
            </p>
            <div className="pm-cta-btns">
              <a href="/#contact" className="sp-btn-white">Get Free Strategy</a>
              <a href="#overview" className="pm-cta-btn-outline">Learn More</a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
